import { useState, useCallback, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  LinearProgress,
  Chip,
} from '@mui/material'
import { Upload, Download } from '@mui/icons-material'
import { importCSV } from '../../api/import'
import { useApi, apiGet } from '../../api/client'
import type { ApiResponse } from '@crm/shared'
import { useAuthStore } from '../../stores/auth.store'

interface FieldInfo {
  name: string
  label: string
  type: string
  is_required: boolean
}

interface MappingRow {
  csv_column: string
  field_name: string
}

export function DataImport() {
  const { data: objectsData } = useApi<{ data: Array<{ id: string; name: string; label: string }> }>('/objects')
  const objects = objectsData?.data ?? []

  const [selectedObject, setSelectedObject] = useState('')
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvHeaders, setCsvHeaders] = useState<string[]>([])
  const [fields, setFields] = useState<FieldInfo[]>([])
  const [mapping, setMapping] = useState<MappingRow[]>([])
  const [importing, setImporting] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [result, setResult] = useState<{ success_count: number; fail_count: number; errors: Array<{ row_number: number; error: string }> } | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (csvHeaders.length > 0 && fields.length > 0) {
      const autoMapping = csvHeaders.map((h) => {
        const match = fields.find((f) => f.name === h || f.label === h)
        return { csv_column: h, field_name: match?.name ?? '' }
      })
      setMapping(autoMapping)
    }
  }, [csvHeaders, fields])

  const MAX_FILE_SIZE_MB = 5

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`)
      return
    }

    setCsvFile(file)
    setResult(null)
    setError('')

    try {
      const text = await file.text()
      const firstLine = text.split('\n')[0]
      const headers = parseCSVLine(firstLine)
      setCsvHeaders(headers)
    } catch {
      setError('Failed to parse CSV file')
    }
  }, [])

  const handleObjectChange = useCallback(async (objectId: string) => {
    setSelectedObject(objectId)
    setResult(null)

    try {
      const data = await apiGet<ApiResponse<{ fields: Array<{ name: string; label: string; type: string; is_required: boolean | number }> }>>(`/objects/${objectId}/schema`)
      const fieldList: FieldInfo[] = (data.data?.fields ?? []).map((f) => ({
        name: f.name,
        label: f.label || f.name,
        type: f.type,
        is_required: !!f.is_required,
      }))
      setFields(fieldList)
    } catch {
      setError('Failed to load fields')
    }
  }, [])

  const handleMappingChange = useCallback((csvCol: string, fieldName: string) => {
    setMapping((prev) => prev.map((m) => m.csv_column === csvCol ? { ...m, field_name: fieldName } : m))
  }, [])

  const handleDownloadTemplate = useCallback(async () => {
    if (!selectedObject) return
    setDownloading(true)
    try {
      const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1'
      const token = useAuthStore.getState().token
      const res = await fetch(`${API_BASE}/settings/import/template?object_id=${selectedObject}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new Error('Download failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const disposition = res.headers.get('Content-Disposition')
      const match = disposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      a.download = match ? decodeURIComponent(match[1].replace(/['"]/g, '')) : 'template.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      setError('Failed to download template')
    } finally {
      setDownloading(false)
    }
  }, [selectedObject])

  const handleImport = useCallback(async () => {
    if (!selectedObject || !csvFile) return
    setImporting(true)
    setError('')

    const activeMapping = mapping.filter((m) => m.field_name && m.field_name !== '$skip')
    try {
      const res = await importCSV(selectedObject, csvFile, activeMapping)
      setResult(res.data ?? (res as unknown as { success_count: number; fail_count: number; errors: Array<{ row_number: number; error: string }> }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed')
    } finally {
      setImporting(false)
    }
  }, [selectedObject, csvFile, mapping])

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Data Import</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {result && (
        <Alert severity={result.fail_count > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
          Import complete: {result.success_count} succeeded, {result.fail_count} failed
          {result.errors.length > 0 && (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {result.errors.slice(0, 10).map((e, i) => (
                <li key={i}>Row {e.row_number}: {e.error}</li>
              ))}
              {result.errors.length > 10 && <li>...and {result.errors.length - 10} more errors</li>}
            </Box>
          )}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Step 1: Select Object & File</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Target Object</InputLabel>
            <Select value={selectedObject} label="Target Object" onChange={(e) => handleObjectChange(e.target.value)}>
              {objects.map((obj) => (
                <MenuItem key={obj.id} value={obj.id}>{obj.label || obj.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="outlined" component="label" startIcon={<Upload />} disabled={!selectedObject}>
            Choose CSV
            <input type="file" hidden accept=".csv" onChange={handleFileSelect} />
          </Button>

          <Button variant="outlined" startIcon={<Download />} disabled={!selectedObject || downloading} onClick={handleDownloadTemplate}>
            {downloading ? 'Downloading...' : 'Download Template'}
          </Button>

          {csvFile && <Chip label={csvFile.name} onDelete={() => { setCsvFile(null); setCsvHeaders([]); setMapping([]) }} />}
        </Box>
      </Paper>

      {csvHeaders.length > 0 && fields.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Step 2: Map Columns</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>CSV Column</TableCell>
                  <TableCell>CRM Field</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {csvHeaders.map((header) => (
                  <TableRow key={header}>
                    <TableCell>{header}</TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={mapping.find((m) => m.csv_column === header)?.field_name ?? '$skip'}
                          onChange={(e) => handleMappingChange(header, e.target.value)}
                        >
                          <MenuItem value="$skip">-- Skip --</MenuItem>
                          {fields.map((f) => (
                            <MenuItem key={f.name} value={f.name}>
                              {f.label} {f.is_required && '*'} ({f.type})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {csvHeaders.length > 0 && selectedObject && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={handleImport} disabled={importing || mapping.every((m) => !m.field_name || m.field_name === '$skip')}>
            {importing ? 'Importing...' : 'Start Import'}
          </Button>
          {importing && <LinearProgress sx={{ flexGrow: 1 }} />}
        </Box>
      )}
    </Box>
  )
}

function parseCSVLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        current += char
      }
    } else {
      if (char === '"') {
        inQuotes = true
      } else if (char === ',') {
        result.push(current.trim())
        current = ''
      } else if (char === '\r' || char === '\n') {
        break
      } else {
        current += char
      }
    }
  }
  result.push(current.trim())
  return result
}
